import { useState, useEffect } from 'react';
import { getBrowserClient } from '@/lib/integrations/supabase/client';
import type { Profile } from '@/lib/integrations/supabase/types';
import * as Sentry from "@sentry/nextjs";

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [permission, setPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      setPermission(Notification.permission);

      navigator.serviceWorker.ready.then((registration) => {
        registration.pushManager.getSubscription().then((sub) => {
          setSubscription(sub);
        });
      });
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const currentPermission = Notification.permission;
        if (currentPermission !== permission) {
          setPermission(currentPermission);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleFocus = () => {
      const currentPermission = Notification.permission;
      if (currentPermission !== permission) {
        setPermission(currentPermission);
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [permission]);

  const subscribe = async () => {
    if (!isSupported || typeof window === 'undefined') {
      return;
    }

    try {
      let registration: ServiceWorkerRegistration;

      const existingRegistrations = await navigator.serviceWorker.getRegistrations();

      if (existingRegistrations.length > 0) {
        registration = existingRegistrations[0];
      } else {
        try {
          registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        } catch (swError) {
          Sentry.captureException(swError);
          registration = await navigator.serviceWorker.ready;
        }
      }

      registration = await navigator.serviceWorker.ready;

      const permissionResult = await Notification.requestPermission();

      setPermission(permissionResult);

      setTimeout(() => {
        const actualPermission = Notification.permission;
        if (actualPermission !== permissionResult) {
          setPermission(actualPermission);
        }
      }, 100);

      if (permissionResult !== 'granted') {
        return;
      }

      const vapidKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;

      if (!vapidKey) {
        Sentry.logger.warn('VAPID public key not found');
        return;
      }

      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      setSubscription(sub);

      const supabase = getBrowserClient();
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('business_id')
        .eq('id', session.user.id)
        .single<Pick<Profile, 'business_id'>>();

      if (profileError || !profile) {
        Sentry.captureException(profileError);
        return;
      }

      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          subscription: sub,
          businessId: profile.business_id,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to subscribe');
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'push-notifications', action: 'subscribe' },
        extra: { error: error instanceof Error ? error.message : String(error) },
      });
      throw error;
    }
  };

  const unsubscribe = async () => {
    if (!subscription || typeof window === 'undefined') return;

    try {
      await subscription.unsubscribe();

      const supabase = getBrowserClient();
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('business_id')
          .eq('id', session.user.id)
          .single<Pick<Profile, 'business_id'>>();

        await fetch('/api/push/unsubscribe', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            subscription: subscription.toJSON(),
            businessId: profile?.business_id,
          }),
        });
      }

      setSubscription(null);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return {
    isSupported,
    permission,
    subscription,
    subscribe,
    unsubscribe
  };
}
